import { useEffect, useRef, useState } from "react";
import { apiApont, type LancamentoProduto, type SucataPontasOpcoes } from "../lib/api";
import { MsgModal } from "./MsgModal";
import { Confirma, Modal, enterTab, parseBR, soNum } from "./EstoqueUi";
import { CamposEncarregado, type EncarregadoToken } from "./SenhaEncarregadoModal";

// acompanhamento_pcf / lancamento_produtos "Cadastro de informações do lançamento de produto - Etapas da Produção" +
// local_estoque (local + encarregado; reunião 28/10/2026: token temporário no lugar da senha). Legado novo (C:\formsteel_d), 22/10/2026. Abre no Finalizar da Inspeção
// Final quando a PCF não tem lotes lançados; Confirmar devolve o lançamento para a finalização gravar tudo numa transação.
// As validações são as do legado (lp:747-820) e o servidor revalida.

interface Props {
  numpcf: number;
  etapa: string;
  posicao: number;
  onConfirmar: (l: LancamentoProduto) => void;
  onVoltar: () => void;
}

export function LancamentoProdutoModal({ numpcf, etapa, posicao, onConfirmar, onVoltar }: Props) {
  const [f, setF] = useState({ unidade: "", peso: "", compmin: "", compmax: "", qtdpec: "", metros: "", sucata: "", compminP: "", compmaxP: "", pontas: "" });
  const [local, setLocal] = useState<{ codloc: number; nomloc: string; autorizacao: string } | null>(null);
  const [abrirLocal, setAbrirLocal] = useState(false);
  const [nompro, setNompro] = useState("");
  // reunião 28/10/2026: pontas -> lote de origem; sucata -> lote da liga
  const [op, setOp] = useState<SucataPontasOpcoes | null>(null);
  const [origem, setOrigem] = useState("");
  const [liga, setLiga] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const raiz = useRef<HTMLDivElement>(null);
  const set = (k: keyof typeof f, casas: number) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: soNum(e.target.value, casas) });

  useEffect(() => {
    apiApont.lancamentoDados(numpcf).then((d) => {
      setNompro(d.nompro ?? "");
      const o = d.sucataPontas ?? null;
      setOp(o);
      if (o?.lotesOrigem.length === 1) setOrigem(String(o.lotesOrigem[0]!.numlot));
      if (o?.ligaSugerida) setLiga(String(o.ligaSugerida));
    }).catch(() => setNompro(""));
  }, [numpcf]);

  // botao-confirmar (lp:740-783) + MOVER-CAMPOS (lp:786-820)
  function confirmar() {
    const v = (s: string) => parseBR(s);
    if (!v(f.compmin)) return setErro("Comprimento mínimo não informadas!");
    if (!v(f.compmax)) return setErro("Comprimento máximo não informado!");
    if (!v(f.peso) || !v(f.qtdpec)) return setErro("Quantidades Produzidas não informada!");
    if (!f.unidade) return setErro("Unidade não informada!");
    if (!local) return setErro("Local de estoque não selecionado não informada!");
    if (v(f.pontas) > 0 && !Number(origem)) return setErro("Informe o lote de origem das pontas.");
    if (v(f.sucata) > 0 && !Number(liga)) return setErro("Informe a liga (AISI) da sucata.");
    onConfirmar({
      unidade: f.unidade, peso: v(f.peso), qtdpec: v(f.qtdpec), metros: v(f.metros), compmin: v(f.compmin), compmax: v(f.compmax),
      codloc: local.codloc, autorizacaoEncarregado: local.autorizacao, sucata: v(f.sucata), pontas: v(f.pontas),
      compminPontas: v(f.compminP), compmaxPontas: v(f.compmaxP), numlotOrigemPontas: Number(origem) || 0, aisiSucata: Number(liga) || 0,
    });
  }

  const origemPc = !!op?.lotesOrigem.find((o) => String(o.numlot) === origem && !o.kg);
  const loteLiga = op?.lotesSucata.find((x) => String(x.aisi) === liga);
  const campo = (rot: string, k: keyof typeof f, casas: number, sm = true) => (
    <label className={"pcf-campo" + (sm ? " pcf-sm" : "")}>
      <span>{rot}</span>
      <input className="num" inputMode={casas ? "decimal" : "numeric"} value={f[k]} onChange={set(k, casas)} />
    </label>
  );
  return (
    <Modal titulo="Lançamento de Produto" sub={`PCF nº ${numpcf} · Etapa: ${etapa} · Posição nº ${posicao}${nompro ? " · " + nompro : ""}`} largura={980} onClose={onVoltar}>
      <MsgModal texto={erro} tipo="erro" onClose={() => setErro(null)} />
      <div ref={raiz} onKeyDown={(e) => enterTab(e, raiz.current)}>
        <section className="pcf-sec">
          <h3 style={{ margin: "0 0 8px", fontSize: 14 }}>PRODUTO FINAL</h3>
          <div className="pcf-grid">
            <label className="pcf-campo pcf-sm">
              <span>Unidade</span>
              <select autoFocus value={f.unidade} onChange={(e) => setF({ ...f, unidade: e.target.value })}>
                <option value=""></option>
                <option>KG</option>
                <option>PÇ</option>
              </select>
            </label>
            {campo("Peso", "peso", 2)}
            {campo("Comp. Mínimo", "compmin", 2)}
            {campo("Comp. Máximo", "compmax", 2)}
            {campo("Peças", "qtdpec", 0)}
            {campo("Metros", "metros", 2)}
            <label className="pcf-campo"><span>Local do Estoque</span><input value={local ? `${local.codloc} · ${local.nomloc}` : ""} readOnly tabIndex={-1} /></label>
            <button className="btn-secundario" onClick={() => setAbrirLocal(true)}>Local de Estoque</button>
          </div>
        </section>
        <section className="pcf-sec">
          <h3 style={{ margin: "0 0 8px", fontSize: 14 }}>SUCATA</h3>
          <div className="pcf-grid">
            {campo("Sucata (Kg)", "sucata", 2)}
            <label className="pcf-campo pcf-sm"><span>Liga (AISI)</span>
              <select value={liga} onChange={(e) => setLiga(e.target.value)}>
                <option value=""></option>
                {(op?.ligas ?? []).map((l) => <option key={l.codigo} value={l.codigo}>{l.nome}</option>)}
              </select></label>
            <label className="pcf-campo"><span>Lote de sucata da liga</span><input readOnly tabIndex={-1} value={loteLiga ? `${loteLiga.numlot} · ${loteLiga.nomloc}` : liga ? "novo lote (no local do produto)" : ""} /></label>
          </div>
        </section>
        <section className="pcf-sec">
          <h3 style={{ margin: "0 0 8px", fontSize: 14 }}>PONTAS</h3>
          <div className="pcf-grid">
            {campo("Comp. Mínimo", "compminP", 2)}
            {campo("Comp. Máximo", "compmaxP", 2)}
            {campo(origemPc ? "Peças" : "Peso", "pontas", origemPc ? 0 : 2)}
            <label className="pcf-campo"><span>Lote de origem (volta ao saldo)</span>
              <select value={origem} onChange={(e) => setOrigem(e.target.value)}>
                <option value="">{op?.lotesOrigem.length ? "" : "sem matéria-prima"}</option>
                {(op?.lotesOrigem ?? []).map((o) => <option key={o.numlot} value={o.numlot}>{o.numlot} · {o.unidade} · {o.nompro}</option>)}
              </select></label>
          </div>
        </section>
        <div className="pcf-acoes">
          <button className="btn-acao verde" onClick={confirmar}>Confirmar</button>
          <span className="spacer" />
          <button className="btn-secundario" onClick={onVoltar}>Voltar</button>
        </div>
      </div>
      {abrirLocal && (
        <LocalEstoqueModal
          onConfirmar={(l) => {
            setLocal(l);
            setAbrirLocal(false);
          }}
          onVoltar={() => setAbrirLocal(false)}
        />
      )}
    </Modal>
  );
}

// local_estoque (le:120-283): local (lupa = locais ATIVOS) + encarregado; "<nome> deseja confirmar o local de estoque?"
// reunião 28/10/2026: a senha do encarregado virou TOKEN temporário do celular; o servidor valida (queima o código) e devolve
// uma autorização de uso único que a finalização usa.
function LocalEstoqueModal({ onConfirmar, onVoltar }: { onConfirmar: (l: { codloc: number; nomloc: string; autorizacao: string }) => void; onVoltar: () => void }) {
  const [codloc, setCodloc] = useState("");
  const [enc, setEnc] = useState<EncarregadoToken>({ codusu: 0, token: "" });
  const tokenRef = useRef<HTMLInputElement>(null);
  const [locais, setLocais] = useState<{ codloc: number; nomloc: string }[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [pergunta, setPergunta] = useState<{ texto: string; nomloc: string; autorizacao: string } | null>(null);
  const raiz = useRef<HTMLDivElement>(null);
  useEffect(() => {
    apiApont.lancamentoDados(0).then((d) => setLocais(d.locais ?? [])).catch(() => setLocais([]));
  }, []);
  async function confirmar() {
    try {
      if (!enc.codusu) return setErro("Encarregado não informado!");
      if (enc.token.length !== 6) return setErro("Token do encarregado não informado (6 dígitos)!");
      const r = await apiApont.localEstoque(Number(codloc) || 0, enc.codusu, enc.token);
      setEnc((v) => ({ ...v, token: "" }));
      if (!r.ok) return setErro(r.erro ?? "Falha na validação.");
      setPergunta({ texto: `${(r.nomusu ?? "").split(" ")[0]} deseja confirmar o local de estoque?`, nomloc: r.nomloc ?? "", autorizacao: r.autorizacao ?? "" });
    } catch (e) {
      setErro((e as Error).message);
    }
  }
  const nome = locais.find((l) => l.codloc === Number(codloc))?.nomloc ?? "";
  return (
    <Modal titulo="Local de Estoque" sub="Confirmação do encarregado" largura={520} onClose={onVoltar}>
      <MsgModal texto={erro} tipo="erro" onClose={() => setErro(null)} />
      <Confirma texto={pergunta?.texto ?? null} onSim={() => pergunta && onConfirmar({ codloc: Number(codloc), nomloc: pergunta.nomloc, autorizacao: pergunta.autorizacao })} onNao={() => setPergunta(null)} />
      <div ref={raiz} onKeyDown={(e) => enterTab(e, raiz.current)}>
        <div className="pcf-grid">
          <label className="pcf-campo">
            <span>Local de Estoque</span>
            <select autoFocus value={codloc} onChange={(e) => setCodloc(e.target.value)}>
              <option value=""></option>
              {locais.map((l) => (
                <option key={l.codloc} value={l.codloc}>{l.codloc} · {l.nomloc}</option>
              ))}
            </select>
          </label>
        </div>
        <CamposEncarregado valor={enc} onChange={setEnc} onEnterToken={() => void confirmar()} tokenRef={tokenRef} autoFocus={false} />
        {nome && <p className="pcf-hint">{nome}</p>}
        <div className="pcf-acoes">
          <button className="btn-acao verde" onClick={() => void confirmar()}>Confirmar</button>
          <span className="spacer" />
          <button className="btn-secundario" onClick={onVoltar}>Voltar</button>
        </div>
      </div>
    </Modal>
  );
}
